import { action, computed, makeObservable } from 'mobx';

import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import { FormStore } from '@/stores/local/shared/form/FormStore';

type CaptionTextEntity = {
  caption: string;
  text: string;
};

type FieldsMap = {
  caption: FieldTextStore;
  text: FieldTextStore;
};

type PrivateFields = '_caption' | '_text';

type FormCreateCaptionTextStoreParams<T extends CaptionTextEntity> = {
  initialEntity?: T | null;
  captionDefaultValue: string;
  captionFallbackValue?: string;
  textDefaultValue?: string;
  sanitizeText?: boolean;
};

export class FormCreateCaptionTextStore<T extends CaptionTextEntity> extends FormStore<FieldsMap> {
  fieldsMap: FieldsMap;

  private readonly captionFallbackValue: string;
  private readonly textFallbackValue: string;

  constructor({
    initialEntity = null,
    captionDefaultValue,
    captionFallbackValue,
    textDefaultValue = '',
    sanitizeText = false,
  }: FormCreateCaptionTextStoreParams<T>) {
    super();

    this.captionFallbackValue = captionFallbackValue ?? captionDefaultValue;
    this.textFallbackValue = textDefaultValue;

    this.fieldsMap = {
      caption: new FieldTextStore({ defaultValue: captionDefaultValue, required: true }),
      text: new FieldTextStore({ defaultValue: textDefaultValue, sanitize: sanitizeText }),
    };

    makeObservable<FormCreateCaptionTextStore<T>, PrivateFields>(this, {
      _caption: computed,
      _text: computed,
      isSubmitDisabled: computed,
      serverFormat: computed,
      setInitialEntity: action.bound,
    });

    this.setInitialEntity(initialEntity);
  }

  private get _caption(): string {
    return (this.fieldsMap.caption.serverFormat ?? '').trim();
  }

  private get _text(): string {
    return (this.fieldsMap.text.serverFormat ?? '').trim();
  }

  get isSubmitDisabled(): boolean {
    return this._caption.length === 0;
  }

  get serverFormat() {
    return {
      caption: this._caption,
      text: this._text,
    };
  }

  submit() {}

  setInitialEntity(entity: T | null) {
    const caption = entity?.caption ?? this.captionFallbackValue;
    const text = entity?.text ?? this.textFallbackValue;

    this.fieldsMap.caption.setValue(caption, { noTouch: true });
    this.fieldsMap.caption.setDefaultValue(caption);

    this.fieldsMap.text.setValue(text, { noTouch: true });
    this.fieldsMap.text.setDefaultValue(text);
  }

  destroy(): void {
    super.destroy();
  }
}
